import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  message,
  Popconfirm,
  Typography,
  Alert,
  Timeline,
  Modal,
  Spin,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DataScienceJobApi,
  DataScienceJob,
  TaskStatus,
} from "../../api/DataScienceJobApi";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import AdminValidation from "../../components/adminvalidation/AdminValidation";
import JobOriginIndicator from "./JobOriginIndicator";
import {
  downloadTaskLogsFromServer,
  TASK_LOGS_MODAL_WIDTH,
  taskLogsModalBodyStyle,
  taskLogsModalStyle,
  taskLogsScrollContainerStyle,
} from "./taskLogsHelper";
import TaskLogLines, { canViewTaskLogs } from "./TaskLogLines";
import {
  getTaskProfileBaseLabel,
  isNonRecommendedTaskProfile,
} from "./taskProfileHelper";
import { getDataScienceJobTypes, isProvisionedSingletonSystemJob } from "./dataScienceJobTypes";
import TaskLogsModalTitle from "./TaskLogsModalTitle";
import { getSelectedOrganizationId, hasWorkingOrganization } from "../../api/Helper";
import { getApiErrorMessage } from "../../helpers/ApiErrorHelper";

const { Title, Text, Paragraph } = Typography;

const ViewDataScienceJob: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [job, setJob] = useState<DataScienceJob | null>(null);
  const [tasks, setTasks] = useState<TaskStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [logs, setLogs] = useState<
    Array<{ timestamp: number; message: string }>
  >([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsDownloading, setLogsDownloading] = useState(false);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);

  const loadTasks = useCallback(
    async (jobType?: string) => {
      if (!jobType) return;

      try {
        const tasksData = await DataScienceJobApi.listTasks({
          job_type: jobType,
          page: 1,
          pageSize: 10,
        });

        let tasks: TaskStatus[] = [];
        if (Array.isArray(tasksData)) {
          tasks = tasksData;
        } else if (tasksData && typeof tasksData === "object") {
          tasks = tasksData.items || [];
        }

        setTasks(tasks);
      } catch (error) {
        message.error(t("DataScienceJobs-LoadTasksError"));
      }
    },
    [t]
  );

  const loadJob = useCallback(
    async (jobId: number) => {
      try {
        setLoading(true);
        const jobData = await DataScienceJobApi.getJob(jobId);
        if (
          hasWorkingOrganization() &&
          Number(getSelectedOrganizationId()) !== jobData.organization_id
        ) {
          message.error(t("DataScienceJobs-JobNotFound"));
          navigate("/admin/datasciencejobs");
          return;
        }
        setJob(jobData);
        await loadTasks(jobData.job_type);
      } catch (error) {
        message.error(t("DataScienceJobs-LoadJobsError"));
        navigate("/admin/datasciencejobs");
      } finally {
        setLoading(false);
      }
    },
    [navigate, t, loadTasks]
  );

  useEffect(() => {
    if (id) {
      loadJob(parseInt(id));
    }
  }, [id, loadJob]);

  const handleExecuteJob = async () => {
    if (!job) return;

    try {
      await DataScienceJobApi.executeJob(job.id);
      message.success(t("DataScienceJobs-ExecuteJobSuccess"));
      loadJob(job.id);
      setTimeout(async () => {
        await loadTasks(job.job_type);
      }, 1000);
    } catch (error) {
      message.error(
        getApiErrorMessage(error, t("DataScienceJobs-ExecuteJobError"))
      );
    }
  };

  const handleReconcileTask = async (taskId: string) => {
    try {
      setLoading(true);
      await DataScienceJobApi.reconcileTask(taskId);
      message.success(t("DataScienceJobs-ReconcileSuccess"));
      await loadTasks(job?.job_type);
    } catch (error) {
      message.error(t("DataScienceJobs-ReconcileError"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewLogs = async (taskId: string) => {
    setSelectedTaskId(taskId);
    setLogsModalVisible(true);
    setLogs([]);
    setNextToken(undefined);
    await loadLogs(taskId);
  };

  const selectedTaskJobLabel = job
    ? t(`DataScienceJobs-JobTypes.${job.job_type}`) || job.job_type
    : "";

  const loadLogs = async (taskId: string, loadMore: boolean = false) => {
    try {
      setLogsLoading(true);
      const response = await DataScienceJobApi.getTaskLogs(
        taskId,
        loadMore ? nextToken : undefined,
        200
      );

      const hasMore =
        Boolean(response.nextToken) && response.events.length > 0;

      if (loadMore) {
        if (response.events.length > 0) {
          setLogs((prev) => [...prev, ...response.events]);
        }
      } else {
        setLogs(response.events);
      }
      setNextToken(hasMore ? response.nextToken : undefined);
    } catch (error) {
      message.error(t("DataScienceJobs-LoadLogsError"));
    } finally {
      setLogsLoading(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isNearBottom && nextToken && !logsLoading && selectedTaskId) {
      loadLogs(selectedTaskId, true);
    }
  };

  const formatLogTimestamp = (timestamp: number) => {
    return format(new Date(timestamp), "dd/MM/yyyy HH:mm:ss", { locale: es });
  };

  const handleDownloadLogs = async () => {
    if (!selectedTaskId) return;

    try {
      setLogsDownloading(true);
      await downloadTaskLogsFromServer(selectedTaskId);
      message.success(t("Common-DownloadSuccessMessage"));
    } catch (error) {
      message.error(t("DataScienceJobs-DownloadLogsError"));
    } finally {
      setLogsDownloading(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!job) return;

    try {
      await DataScienceJobApi.deleteJob(job.id);
      message.success(t("DataScienceJobs-DeleteJobSuccess"));
      navigate("/admin/datasciencejobs");
    } catch (error) {
      message.error(
        getApiErrorMessage(error, t("DataScienceJobs-DeleteJobError"))
      );
    }
  };

  const getStatusTag = (status?: string) => {
    if (!status) return <Tag color="default">{t("Common-Unknown")}</Tag>;

    const statusConfig = {
      pending: { color: "processing", icon: <ClockCircleOutlined /> },
      running: { color: "processing", icon: <ReloadOutlined spin /> },
      success: { color: "success", icon: <CheckCircleOutlined /> },
      failed: { color: "error", icon: <CloseCircleOutlined /> },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: "default",
      icon: null,
    };

    return (
      <Tag color={config.color} icon={config.icon}>
        {t(`DataScienceJobs-Statuses.${status}`) || status}
      </Tag>
    );
  };

  const getJobTypeTag = (jobType: string) => (
    <Tag color="blue">
      {t(`DataScienceJobs-JobTypes.${jobType}`) || jobType}
    </Tag>
  );

  const formatDate = (date?: string) => {
    if (!date) return "-";
    return format(new Date(date), "dd/MM/yyyy HH:mm:ss", { locale: es });
  };

  const parseParameters = (parameters?: string) => {
    if (!parameters) return {};
    try {
      return JSON.parse(parameters);
    } catch {
      return { error: "Invalid JSON" };
    }
  };

  const getRecentTasks = () => {
    return tasks
      .filter((task) => task.status !== "pending")
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      .slice(0, 5);
  };

  if (!job) {
    return (
      <div style={{ padding: "24px" }}>
        <Card loading={loading}>
          <div style={{ height: "200px" }} />
        </Card>
      </div>
    );
  }

  const recentTasks = getRecentTasks();
  const parameters = parseParameters(job.parameters);
  const isDataScienceJob =
    getDataScienceJobTypes(t).find((item) => item.jobType === job.job_type)
      ?.origin === "data_science";
  const taskProfile = parameters.task_profile as string | undefined;
  const jobTypeLabel =
    t(`DataScienceJobs-JobTypes.${job.job_type}`) || job.job_type;
  const jobTypeConfig = getDataScienceJobTypes(t).find(
    (item) => item.jobType === job.job_type
  );
  const executeWarningKey = jobTypeConfig?.executeWarningKey;
  const isSingletonSystemJob = jobTypeConfig?.singletonSystemJob === true;
  const isProvisionedSingleton =
    isSingletonSystemJob && isProvisionedSingletonSystemJob(job);

  return (
    <AdminValidation>
      <div style={{ padding: "24px" }}>
        <div
          style={{
            marginBottom: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/admin/datasciencejobs")}
            >
              {t("Common-Back")}
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {job.job_name}
            </Title>
          </Space>
          <Space>
            <Popconfirm
              title={
                executeWarningKey ? (
                  <div style={{ maxWidth: 320 }}>
                    <div>{t("DataScienceJobs-ConfirmExecute")}</div>
                    <div style={{ marginTop: 4, color: "#faad14" }}>
                      {t(executeWarningKey)}
                    </div>
                  </div>
                ) : (
                  t("DataScienceJobs-ConfirmExecute")
                )
              }
              onConfirm={handleExecuteJob}
              okText={t("Common-Yes")}
              cancelText={t("Common-No")}
              disabled={!job.is_active}
            >
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                disabled={!job.is_active}
              >
                {t("DataScienceJobs-Execute")}
              </Button>
            </Popconfirm>
            {!isSingletonSystemJob && (
              <Button
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(
                    `/admin/datasciencejobs/${job.job_type}/${job.id}/edit`
                  )
                }
              >
                {t("DataScienceJobs-Edit")}
              </Button>
            )}
            {!isProvisionedSingleton && (
              <Popconfirm
                title={t("DataScienceJobs-ConfirmDelete")}
                onConfirm={handleDeleteJob}
                okText={t("Common-Yes")}
                cancelText={t("Common-No")}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t("DataScienceJobs-Delete")}
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: "24px",
          }}
        >
          <div>
            <Card
              title={t("DataScienceJobs-JobName")}
              style={{ marginBottom: "24px" }}
            >
              <Descriptions column={1} bordered>
                <Descriptions.Item label={t("DataScienceJobs-JobName")}>
                  {job.job_name}
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-JobType")}>
                  {getJobTypeTag(job.job_type)}
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-OriginLabel")}>
                  <JobOriginIndicator
                    origin={job.origin ?? "data_science"}
                    size={16}
                  />
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-Organization")}>
                  {job.organization_id}
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-IsActive")}>
                  <Tag color={job.is_active ? "green" : "red"}>
                    {job.is_active ? t("Common-Active") : t("Common-Inactive")}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-CronExpression")}>
                  <Text code>
                    {job.cron_expression || t("DataScienceJobs-Unscheduled")}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-LastStatus")}>
                  {getStatusTag(job.last_status)}
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-ExecutionCount")}>
                  {job.execution_count}
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-LastExecution")}>
                  {formatDate(job.last_execution)}
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-NextExecution")}>
                  {formatDate(job.next_execution)}
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-CreatedAt")}>
                  {formatDate(job.created_at)}
                </Descriptions.Item>
                <Descriptions.Item label={t("DataScienceJobs-UpdatedAt")}>
                  {formatDate(job.updated_at)}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              title={t("DataScienceJobs-Parameters")}
              style={{ marginBottom: "24px" }}
            >
              {isDataScienceJob && taskProfile && (
                <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
                  <Descriptions.Item label={t("DataScienceJobs-TaskProfile")}>
                    {getTaskProfileBaseLabel(t, taskProfile)}
                  </Descriptions.Item>
                </Descriptions>
              )}
              {isDataScienceJob &&
                isNonRecommendedTaskProfile(job.job_type, taskProfile) && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t("DataScienceJobs-TaskProfile.nonRecommendedWarning", {
                      jobType: jobTypeLabel,
                    })}
                  />
                )}
              <pre
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "16px",
                  borderRadius: "6px",
                  overflow: "auto",
                  maxHeight: "300px",
                }}
              >
                {JSON.stringify(parameters, null, 2)}
              </pre>
            </Card>

            {job.last_error && (
              <Card
                title={t("DataScienceJobs-LastError")}
                style={{ marginBottom: "24px" }}
              >
                <Alert
                  message={t("DataScienceJobs-LastError")}
                  description={job.last_error}
                  type="error"
                  showIcon
                />
              </Card>
            )}
          </div>

          <div>
            <Card
              title={t("DataScienceJobs-Status")}
              style={{ marginBottom: "24px" }}
            >
              <Timeline>
                {recentTasks.map((task, index) => (
                  <Timeline.Item
                    key={task.task_id}
                    color={
                      task.status === "success"
                        ? "green"
                        : task.status === "failed"
                        ? "red"
                        : task.status === "running"
                        ? "blue"
                        : "gray"
                    }
                    dot={
                      task.status === "running" ? (
                        <ReloadOutlined spin />
                      ) : undefined
                    }
                  >
                    <div>
                      <div
                        style={{
                          marginBottom: "8px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          {getStatusTag(task.status)}
                          {task.progress && (
                            <Text type="secondary"> - {task.progress}%</Text>
                          )}
                        </div>
                        <Space size="small">
                          {canViewTaskLogs(task) && (
                            <Button
                              size="small"
                              icon={<EyeOutlined />}
                              onClick={() => handleViewLogs(task.task_id)}
                              title={t("DataScienceJobs-ViewLogs")}
                            >
                              {t("DataScienceJobs-Logs")}
                            </Button>
                          )}
                          {task.execution_id && task.status === "running" && (
                            <Button
                              size="small"
                              icon={<SyncOutlined />}
                              loading={loading}
                              onClick={() => handleReconcileTask(task.task_id)}
                              title={t("DataScienceJobs-ReconcileTask")}
                              style={{ fontSize: "11px", padding: "2px 6px" }}
                            >
                              {t("DataScienceJobs-Reconcile")}
                            </Button>
                          )}
                        </Space>
                      </div>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        {formatDate(task.created_at)}
                      </Text>
                      {job.job_type === "demand_forecast" &&
                        task.artifact_snapshot && (
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {t("DataScienceJobs-ArtifactsUsed")}
                              {task.artifact_snapshot.source === "image_default"
                                ? ` (${t("DataScienceJobs-ArtifactImageDefault")})`
                                : ""}
                            </Text>
                            <div>
                              <Text style={{ fontSize: "12px" }}>
                                {t("DataScienceJobs-ArtifactPipeline")}:{" "}
                                {task.artifact_snapshot.pipelineFilename || "-"}
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: "12px" }}>
                                {t("DataScienceJobs-ArtifactModel")}:{" "}
                                {task.artifact_snapshot.modelFilename || "-"}
                              </Text>
                            </div>
                            <div>
                              <Text style={{ fontSize: "12px" }}>
                                {t("DataScienceJobs-ArtifactConfig")}:{" "}
                                {task.artifact_snapshot.configFilename || "-"}
                              </Text>
                            </div>
                          </div>
                        )}
                      {task.message && (
                        <Paragraph
                          style={{ marginTop: "4px", fontSize: "12px" }}
                        >
                          {task.message}
                        </Paragraph>
                      )}
                    </div>
                  </Timeline.Item>
                ))}
                {recentTasks.length === 0 && (
                  <Timeline.Item>
                    <Text type="secondary">
                      {t("DataScienceJobs-NoTasksFound")}
                    </Text>
                  </Timeline.Item>
                )}
              </Timeline>
            </Card>
          </div>
        </div>
      </div>

      {/* Logs Modal */}
      <Modal
        title={
          <TaskLogsModalTitle
            title={`${t("DataScienceJobs-TaskLogs")}${selectedTaskJobLabel ? ` - ${selectedTaskJobLabel}` : ""} - ${selectedTaskId}`}
            onDownload={handleDownloadLogs}
            downloading={logsDownloading}
            downloadDisabled={!selectedTaskId}
            downloadTooltip={t("Common-Download")}
          />
        }
        visible={logsModalVisible}
        onCancel={() => setLogsModalVisible(false)}
        width={TASK_LOGS_MODAL_WIDTH}
        style={taskLogsModalStyle}
        bodyStyle={taskLogsModalBodyStyle}
        footer={[
          <Button key="close" onClick={() => setLogsModalVisible(false)}>
            {t("Common-Close")}
          </Button>,
        ]}
      >
        <div
          style={taskLogsScrollContainerStyle}
          onScroll={handleScroll}
        >
          <TaskLogLines
            logs={logs}
            logsLoading={logsLoading}
            nextToken={nextToken}
            formatTimestamp={formatLogTimestamp}
            t={t}
          />
        </div>
      </Modal>
    </AdminValidation>
  );
};

export default ViewDataScienceJob;
